/**
 * MUS-393 - roadmap "Play" routing: tutorial vs. gameplay.
 *
 * The roadmap Play action must decide whether pressing Play on a level drops the
 * user into the tutorial or straight into gameplay (the sight-reading lesson).
 * That decision lived, duplicated, in `PlayContainer.tsx` (the roadmap Play bar)
 * and `Path.tsx` (the level-preview sheet's Play and the level double-click),
 * each testing `highest_tier_complete >= 1`.
 *
 * `highest_tier_complete` only increments when a TIER is completed. Finishing a
 * level's TUTORIAL does not touch it (except for level 0, whose tutorial-end
 * handler auto-sets it to 1). So a user on any non-zero level who finished the
 * tutorial but has not yet cleared tier 1 still had `highest_tier_complete = 0`,
 * and the old predicate routed them back into the tutorial - which resumes at the
 * saved last chapter, i.e. the final chapter of a fully-watched tutorial. "Tier
 * complete" was standing in for "tutorial complete," and the window between the
 * two was the bug.
 *
 * The fix routes to gameplay when the level's TUTORIAL is complete, deriving that
 * client-side from the completed-chapters set (hydrated at startup by the
 * tutorial-progress fetch into `tutorialReducer.completed`) against the level's
 * authored chapter count (a bundled client constant - no backend/schema change).
 * Every roadmap Play surface now shares this single decision so they cannot drift.
 */
import { Set as ImmutableSet } from 'immutable';
import TutorialData from 'Constants/Tutorial';

/** The per-level completed-chapters value as read from `tutorialReducer.completed`.
 *  It is an `Immutable.Set` once `HYDRATE_COMPLETION` has run at startup, but can
 *  be a plain `number[]` in the window after redux-persist rehydration and before
 *  that merge (the completion map is JSON-serialized, losing its Immutable shape).
 *  Both forms are handled. */
export type CompletedChapters = ImmutableSet<number> | number[] | undefined;

/** Size of a completed-chapters value, tolerant of Immutable.Set or plain array. */
function completedChapterCount(completed: CompletedChapters): number {
  if (!completed) return 0;
  if (Array.isArray(completed)) return completed.length;
  const size = (completed as { size?: number }).size;
  return typeof size === 'number' ? size : 0;
}

/** Total tutorial chapters authored for a level, summed across the bundled
 *  tutorial config's sections. 0 when the level has no interactive tutorial
 *  config (levels beyond the authored set), which safely reads as "not complete"
 *  and defers to the tier-based path. */
export function tutorialChapterCount(levelNumber: number): number {
  const config = TutorialData[levelNumber];
  if (!config) return 0;
  return config.sections.reduce((total, section) => total + section.chapters.length, 0);
}

/** True when every authored tutorial chapter for the level has been completed.
 *  Uses `>=` so a completed set that somehow carries extra ids still counts as
 *  complete. Mirrors the tutorial's own `completedChapters.size === chapterCount`
 *  definition used to auto-complete level 0. */
export function isTutorialComplete(
  levelNumber: number,
  completedChapters: CompletedChapters,
): boolean {
  const total = tutorialChapterCount(levelNumber);
  if (total === 0) return false;
  return completedChapterCount(completedChapters) >= total;
}

/** True when completing `chapter` is what completes the level's tutorial: the
 *  chapters already done (saved from earlier sittings, plus this sitting's) did
 *  not cover every chapter, and with `chapter` they do. Counting earlier sittings
 *  makes a tutorial finished across visits complete exactly once; a level already
 *  complete never completes again. Only indices inside the level count. */
export function completesTutorial(
  doneChapters: Iterable<number>,
  chapter: number,
  chapterCount: number,
): boolean {
  if (chapterCount <= 0) return false;
  const isLevelChapter = (index: number) =>
    Number.isInteger(index) && index >= 0 && index < chapterCount;
  const doneBefore = new Set([...doneChapters].filter(isLevelChapter));
  if (doneBefore.size >= chapterCount || !isLevelChapter(chapter)) return false;
  return new Set([...doneBefore, chapter]).size >= chapterCount;
}

/** The single roadmap Play routing decision shared by every roadmap Play surface
 *  (the Play bar, the level-preview sheet, the level double-click and the staged
 *  roadmap). Returns the path to navigate to.
 *
 *  - Level 0 is tutorial-only (no sight-reading stage): always the tutorial.
 *  - Any non-zero level goes to gameplay once a tier is complete (unchanged
 *    behavior) OR the tutorial has been fully watched (the MUS-393 fix).
 *  - Otherwise the tutorial (unchanged behavior). */
export function resolvePlayRoute(
  levelNumber: number,
  highestTierComplete: number,
  completedChapters: CompletedChapters,
): '/lesson' | '/tutorial' {
  if (levelNumber <= 0) return '/tutorial';
  if (highestTierComplete >= 1 || isTutorialComplete(levelNumber, completedChapters)) {
    return '/lesson';
  }
  return '/tutorial';
}
